import { useGetIntelligenceActionCenterProjectProjectId } from '../api/action-center-api/action-center-api';
import { useGetActionpointProjectIdProject } from '../api/action-point-api/action-point-api';
import { useGetIntelligenceDependenciesProjectProjectIdGraph } from '../api/dependency-api/dependency-api';
import { useGetIntelligenceFindingsProjectProjectId } from '../api/findings-api/findings-api';
import {
  useGetIntelligenceKpisProjectProjectId,
  useGetIntelligenceKpisProjectProjectIdHistory,
} from '../api/kpi-api/kpi-api';
import { HISTORY_KEYS, HISTORY_WINDOW_DAYS, isoDaysAgo } from '../components/ReportMetrics/detail';
import { useProjectMilestones } from './useProjectMilestones';
import { useProjectPhases } from './useProjectPhases';

/**
 * Which of the heavier reads a caller needs. The report pages want everything;
 * the Cockpit band only wants the current values and the alert count, and
 * should not pay for a dependency graph or a findings pass (each a full project
 * snapshot load) to show six tiles.
 */
export interface IntelligenceOptions {
  history?: boolean;
  graph?: boolean;
  milestones?: boolean;
  findings?: boolean;
  actionPoints?: boolean;
}

/**
 * Everything the Project Intelligence pages read, in one hook: the current KPI
 * values, the Action Center alerts, a year of history for the KPIs the pages
 * chart or compare against, the dependency graph, the findings (the objects
 * behind the scores), the project's Action Points, and the phases and
 * milestones that give scoped values a name instead of a bare id.
 *
 * KPI values, alerts, history and findings all derive from the same engine
 * pass, so they agree with each other as long as nothing recomputes in between.
 */
export const useProjectIntelligence = (projectId?: number, options: IntelligenceOptions = {}) => {
  const {
    history: withHistory = true,
    graph = true,
    milestones: withMilestones = true,
    findings: withFindings = true,
    actionPoints: withActionPoints = true,
  } = options;
  const enabled = !!projectId;
  const id = projectId as number;

  const kpis = useGetIntelligenceKpisProjectProjectId(id, undefined, { query: { enabled } });

  const alerts = useGetIntelligenceActionCenterProjectProjectId(id, { query: { enabled } });

  const history = useGetIntelligenceKpisProjectProjectIdHistory(
    id,
    { keys: [...HISTORY_KEYS], from: isoDaysAgo(HISTORY_WINDOW_DAYS) },
    { query: { enabled: enabled && withHistory } },
  );

  const dependencyGraph = useGetIntelligenceDependenciesProjectProjectIdGraph(id, {
    query: { enabled: enabled && graph },
  });

  const findings = useGetIntelligenceFindingsProjectProjectId(id, {
    query: { enabled: enabled && withFindings },
  });

  const actionPoints = useGetActionpointProjectIdProject(id, {
    query: { enabled: enabled && withActionPoints },
  });

  const phases = useProjectPhases(projectId);
  const milestones = useProjectMilestones(withMilestones ? projectId : undefined);

  return {
    kpis: kpis.data,
    alerts: alerts.data,
    history: history.data,
    dependencyGraph: dependencyGraph.data,
    findings: findings.data,
    actionPoints: actionPoints.data,
    phases: phases.data,
    milestones: milestones.data,
    isLoading: kpis.isLoading || alerts.isLoading,
    // The page still renders from whatever did load; a failed trend line or
    // findings list should not blank out the health score.
    isError: kpis.isError,
    refetch: () => {
      void kpis.refetch();
      void alerts.refetch();
      void history.refetch();
      void dependencyGraph.refetch();
      void findings.refetch();
      void actionPoints.refetch();
      void phases.refetch();
      void milestones.refetch();
    },
  };
};
